import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Beaker, ArrowRightLeft, AlertTriangle, CheckCircle2, FlaskConical } from 'lucide-react';
import type { AssessmentResult, DosingConfig } from '@/types/calculator';
import { AVAILABLE_CHEMICALS } from '@/types/calculator';

interface DosingPopupProps {
  result: AssessmentResult;
  onClose: () => void;
  onApply: (dosings: DosingConfig[]) => void;
  currentDosings: DosingConfig[];
}

export default function DosingPopup({ result, onClose, onApply, currentDosings }: DosingPopupProps) {
  const reqs = result.equipmentDosingRequirements ?? [];
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [applied, setApplied] = useState(false);

  if (reqs.length === 0) {
    onClose();
    return null;
  }

  const handleApply = () => {
    const newDosings: DosingConfig[] = [...currentDosings];
    
    for (const req of reqs) {
      const chemId = selected[req.equipmentId];
      if (!chemId) continue;
      const chem = req.recommendedChemicals.find(c => c.chemicalId === chemId);
      if (!chem) continue;

      const existingIdx = newDosings.findIndex(d => d.enabled && d.chemicalId === chemId);
      if (existingIdx >= 0) {
        newDosings[existingIdx] = { ...newDosings[existingIdx], dose: chem.recommendedDose };
      } else {
        newDosings.push({ enabled: true, chemicalId: chemId, dose: chem.recommendedDose });
      }
    }

    onApply(newDosings);
    setApplied(true);
    setTimeout(onClose, 1500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="w-full max-w-2xl max-h-[85vh] overflow-auto rounded-2xl border border-slate-700"
      style={{ background: 'var(--bg-surface)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-slate-700 sticky top-0 z-10" style={{ background: 'var(--bg-surface)' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-rb-brand" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">Dosing Recommendations</h3>
            <p className="text-xs text-slate-500">{reqs.length} equipment{reqs.length > 1 ? 's' : ''} need chemical adjustment</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Warning banner */}
        {result.dosingWarning?.show && (
          <div className={`p-3 rounded-xl border ${
            result.dosingWarning.severity === 'critical'
              ? 'bg-red-500/5 border-red-500/20'
              : 'bg-amber-500/5 border-amber-500/20'
          }`}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                result.dosingWarning.severity === 'critical' ? 'text-red-400' : 'text-amber-400'
              }`} />
              <p className="text-xs text-slate-400 leading-relaxed">{result.dosingWarning.message}</p>
            </div>
          </div>
        )}

        {applied && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 flex items-center gap-2"
          >
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-emerald-400 font-medium">Dosing applied — assessment will update automatically</span>
          </motion.div>
        )}

        {/* Equipment cards */}
        {reqs.map((req) => {
          const selectedId = selected[req.equipmentId] ?? req.recommendedChemicals[0]?.chemicalId;
          return (
            <div key={req.equipmentId} className="p-4 rounded-xl border border-slate-800/50 bg-slate-800/20 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Beaker className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm font-semibold text-white">{req.equipmentName}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold uppercase ${
                    req.severity === 'required'
                      ? 'bg-red-500/10 text-red-400 border-red-500/20'
                      : 'bg-rb-brand/10 text-rb-brand border-rb-brand/20'
                  }`}>
                    {req.severity}
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{req.reason}</p>

              {/* Chemical alternatives */}
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-[10px] text-slate-500 uppercase tracking-wider font-bold">
                  <ArrowRightLeft className="w-3 h-3" />
                  Choose Coagulant
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {req.recommendedChemicals.map((chem) => {
                    const isSelected = selectedId === chem.chemicalId;
                    const chemMeta = AVAILABLE_CHEMICALS.find(c => c.id === chem.chemicalId);
                    return (
                      <button
                        key={chem.chemicalId}
                        onClick={() => setSelected(prev => ({ ...prev, [req.equipmentId]: chem.chemicalId }))}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          isSelected
                            ? 'bg-rb-brand/10 border-rb-brand/40'
                            : 'bg-slate-800/30 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-white">{chem.name}</span>
                          {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-rb-brand" />}
                        </div>
                        <span className="text-[10px] text-slate-500 font-mono">{chem.formula}</span>
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="text-xs font-bold text-cyan-400">{chem.recommendedDose} {chem.doseUnit}</span>
                          {chemMeta && (
                            <span className="text-[10px] text-slate-600">
                              pH {chemMeta.phChangePerMgL > 0 ? '+' : ''}{(chemMeta.phChangePerMgL * chem.recommendedDose).toFixed(1)}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{chem.reason}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-5 border-t border-slate-700 flex items-center justify-between sticky bottom-0 z-10" style={{ background: 'var(--bg-surface)' }}>
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-white transition-colors">
          Skip for now
        </button>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleApply}
          disabled={applied}
          className="px-5 py-2.5 rounded-lg text-sm font-medium bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all disabled:opacity-50"
        >
          Apply Selected Chemicals
        </motion.button>
      </div>
    </motion.div>
  );
}
